import moment from "moment";
import html2canvas from "html2canvas";
import dayjs from "dayjs";
import qz from "qz-tray";
import {
  EMIONBOARDINGSTATUS,
  ExpenseCategoryList,
  VERIFIED,
  getValidStoredLanguage,
} from "./constants";
import { analytics, triggerLogEvent } from "./config/firebase";
import { formatNum } from "./oscar-pos-core/constants";
import { checkConnection } from "./printerConnection";
export const extractId = (item) => item.id;

export const removeLeadingZeros = (value) => {
  return value.replace(/^0+(?=\d)/, ""); // Replace leading zeros
};

export const dateFormat = (date) => {
  return date ? moment(date).format("Do MMMM YYYY") : "";
};
export const dateFormatInNumberWithDash = (date) => {
  return date ? moment(date).format("DD-MM-YYYY") : "";
};
export const formattedDateWithTime = (date) =>
  date ? moment(date).format("MMMM D, YYYY , h:mm A") : "";

export const formattedShortDateWithTime = (date) =>
  date ? moment(date).format("D,MMM, YYYY , h:mm A") : "";

export const getInitials = (name) => {
  if (!name) return "";
  const words = name.trim().split(" ").filter(Boolean);
  if (!words.length) return "";
  if (words.length === 1) {
    return (words[0][0] || "").toUpperCase();
  }
  return (words[0][0] || "").toUpperCase() + (words[1][0] || "").toUpperCase();
};

export const removeRsFromValue = (str) => {
  let result = (str || "").replace(/Rs\.\s*/g, "").trim(); // Removes 'Rs.' with any optional space
  return result;
};

export const validatePriceQtyInput = (value, max = 999999, min = 0) => {
  let result = +removeRsFromValue(value);
  if (result === "" || isNaN(result)) return false;
  if (Number(result) > max || Number(result) < min) return false;

  // Regex to ensure no more than 2 decimal places
  const decimalRegex = /^\d+(\.\d{1,2})?$/;
  if (!decimalRegex.test(result.toString())) return false;

  return true;
};

export const generateDateRange = (option) => {
  return new Promise((resolve, reject) => {
    try {
      const currentDate = moment();
      const dateRanges = [];
      let limit =
        option === "Weekly"
          ? 3
          : option === "Monthly"
            ? 3
            : option === "Yearly"
              ? 11
              : 6;

      for (let i = limit; i >= 0; i--) {
        let startDate, endDate, label;

        switch (option) {
          case "Daily":
            startDate = moment(currentDate)
              .subtract(i, "days")
              .startOf("day")
              .valueOf();
            endDate = moment(currentDate)
              .subtract(i, "days")
              .endOf("day")
              .valueOf();
            label = moment(startDate).format("dd"); // Day name
            break;
          case "Weekly":
            startDate = moment(currentDate)
              .subtract(i, "weeks")
              .startOf("isoWeek")
              .valueOf();
            endDate = moment(currentDate)
              .subtract(i, "weeks")
              .endOf("isoWeek")
              .valueOf();
            label = `Week ${4 - i}`;
            break;
          case "Monthly":
            startDate = moment(currentDate)
              .subtract(i, "months")
              .startOf("month")
              .valueOf();
            endDate = moment(currentDate)
              .subtract(i, "months")
              .endOf("month")
              .valueOf();
            label = moment(startDate).format("MMM"); // Month name
            break;
          case "Yearly":
            startDate = moment(currentDate)
              .subtract(i, "months")
              .startOf("month")
              .valueOf();
            endDate = moment(currentDate)
              .subtract(i, "months")
              .endOf("month")
              .valueOf();
            label = moment(startDate).format("MMM"); // Month name
            break;
          default:
            startDate = moment(currentDate)
              .subtract(i, "days")
              .startOf("day")
              .valueOf();
            endDate = moment(currentDate)
              .subtract(i, "days")
              .endOf("day")
              .valueOf();
            label = moment(startDate).format("dd"); // Day name
        }

        dateRanges.push({ startDate, endDate, label });
      }

      resolve(dateRanges);
    } catch (error) {
      reject(error);
    }
  });
};

export const handleRangeFromFilter = (filter) => {
  let start = new Date().getTime();
  let end = new Date().getTime();
  if (filter == "Today" || filter == "today") {
    start = moment().startOf("day").valueOf();
    end = moment().endOf("day").valueOf();
  } else if (filter == "7 days") {
    start = moment().subtract(7, "days").startOf("day").valueOf();
    end = moment().endOf("day").valueOf();
  } else if (filter == "15 days") {
    start = moment().subtract(15, "days").startOf("day").valueOf();
    end = moment().endOf("day").valueOf();
  } else if (filter == "30 days") {
    start = moment().subtract(30, "days").startOf("day").valueOf();
    end = moment().endOf("day").valueOf();
  } else if (filter == "yesterday") {
    start = moment().subtract(1, "days").startOf("day").valueOf();
    end = moment().subtract(1, "days").endOf("day").valueOf();
  } else if (filter == "week") {
    start = moment().startOf("week").valueOf();
    end = moment().endOf("week").valueOf();
  } else if (filter == "month") {
    start = moment().startOf("month").valueOf();
    end = moment().endOf("month").valueOf();
  } else if (moment(filter).isValid()) {
    start = filter.startOf("day").valueOf();
    end = filter.endOf("day").valueOf();
  }

  return { start, end };
};
export const isValidPin = (pin) => {
  if (!/^\d{4}$/.test(pin)) return false; // Must be exactly 4 digits
  // All digits are same
  if (/^(\d)\1{3}$/.test(pin)) return false;
  const digits = pin.split("").map(Number);
  // Check for ascending sequence
  const isAscending = digits.every(
    (d, i, arr) => i === 0 || d === arr[i - 1] + 1,
  );
  // Check for descending sequence
  const isDescending = digits.every(
    (d, i, arr) => i === 0 || d === arr[i - 1] - 1,
  );
  if (isAscending || isDescending) return false;
  return true;
};

export const getStartAndEndDate = (filter) => {
  const today = moment().startOf("day"); // Start of the current day
  switch (filter) {
    case "All":
      return { startDate: null, endDate: null }; // No filtering
    case "Today":
      return { startDate: today, endDate: today.clone().endOf("day") }; // Current day

    case "5 days":
      return {
        startDate: moment().subtract(4, "days").startOf("day"),
        endDate: today.endOf("day"),
      };

    case "7 days":
      return {
        startDate: moment().subtract(6, "days").startOf("day"),
        endDate: today.endOf("day"),
      };

    case "10 days":
      return {
        startDate: moment().subtract(9, "days").startOf("day"),
        endDate: today.endOf("day"),
      };

    case "15 days":
      return {
        startDate: moment().subtract(14, "days").startOf("day"),
        endDate: today.endOf("day"),
      };
    case "14 days":
      return {
        startDate: moment().subtract(13, "days").startOf("day"),
        endDate: today.endOf("day"),
      };
    case "30 days":
      return {
        startDate: moment().subtract(29, "days").startOf("day"),
        endDate: today.endOf("day"),
      };
    case "Half yearly":
      return {
        startDate: moment().subtract(6, "months").startOf("day"),
        endDate: today.endOf("day"),
      };
    case "Yearly":
      return {
        startDate: moment.utc().subtract(1, "year").startOf("day"),
        endDate: moment().endOf("day"),
      };
    case "This month":
      return {
        startDate: moment().startOf("month"),
        endDate: today.endOf("day"),
      };
    case "Last 3 months":
      return {
        startDate: moment().subtract(3, "months").startOf("day"),
        endDate: today.endOf("day"),
      };
    case "Last 6 months":
      return {
        startDate: moment().subtract(6, "months").startOf("day"),
        endDate: today.endOf("day"),
      };
    case "Last 12 months":
      return {
        startDate: moment().subtract(12, "months").startOf("day"),
        endDate: today.endOf("day"),
      };
    case "Custom":
      // For custom, you can either set the custom start and end dates or handle it accordingly
      return { startDate: null, endDate: null }; // Example, custom logic would be added here
    default:
      return { startDate: null, endDate: null };
  }
};

export const mergeCashbookCategories = (cashbook_categories) => {
  // Start with the default ExpenseCategory names
  const defaultNames = ExpenseCategoryList.map((c) => c.value);

  if (!cashbook_categories?.length) return [...defaultNames];

  // Extract names from cashbook categories
  const cashbookNames = cashbook_categories.map((c) => c.name);

  // Merge & remove duplicates
  const allNames = [
    ...defaultNames,
    ...cashbookNames.filter((name) => !defaultNames.includes(name)),
  ];

  return allNames;
};

export const buildFiltersConfig = (
  filters = [],
  categories = [],
  status,
  contactType,
  placeholder,
  customSort,
  customMethods,
  customView,
  statusType,
  I18n = {}
) => {
  const map = {
    status: {
      id: "status",
      label: I18n.status || "Status",
      type: statusType || "checkbox",
      options: status || [
        { label: I18n.paid || "Paid", value: "paid" },
        { label: I18n.unpaid || "Unpaid", value: "unpaid" },
      ],
    },

    visibility: {
      id: "visibility",
      label: I18n.visibility || "Visibility",
      type: "sort",
      options: [
        { label: I18n.published || "Published", value: "published" },
        { label: I18n.unpublished || "Unpublished", value: "unpublished" },
      ],
    },

    view: {
      id: "view",
      label: I18n.view || "View",
      type: "sort",
      options: customView || [
        { label: I18n.quick_sale || "Quick sale", value: "Quick sale" },
        { label: I18n.invoice || "Invoice", value: "Invoice" },
        { label: I18n.pos || "POS", value: "POS" },
      ],
    },

    customers: {
      id: "customers",
      label: I18n.customers || "Customers",
      type: "sort",
      options: contactType
        ? contactType
        : [
          { label: I18n.customers || "Customers", value: "Customers" },
          { label: I18n.suppliers || "Suppliers", value: "Suppliers" },
          { label: I18n.by_others || "By others", value: "By others" },
        ],
    },
    items: {
      id: "items",
      label: I18n.items || "Items",
      type: "sort",
      options: [{ label: I18n.low_stock_items || "Low stock items", value: "Low stock items" }],
    },
    method: {
      id: "method",
      label: I18n.method || "Method",
      type: "checkbox",
      options: customMethods ? customMethods : [
        { label: I18n.cash || "Cash", value: "Cash" },
        { label: I18n.bank || "Bank", value: "Bank" },
      ],
    },

    category: {
      id: "category",
      label: I18n.category || "Category",
      type: "category",
      options: categories?.length
        ? categories.filter(Boolean)
        : ExpenseCategoryList, // remove empty values
    },

    date: {
      id: "date",
      label: I18n.date || "Date",
      type: "date",
    },

    sort: {
      id: "sort",
      label: I18n.sort || "Sort",
      type: "sort",
      options: customSort
        ? customSort
        : [
          { label: I18n.newest || "Newest", value: "Newest" },
          { label: I18n.oldest || "Oldest", value: "Oldest" },
          { label: I18n.amount_high_to_low || "Amount (High to Low)", value: "Amount (High to Low)" },
          { label: I18n.amount_low_to_high || "Amount (Low to High)", value: "Amount (Low to High)" },
        ],
    },

    type: {
      id: "type",
      label: I18n.type || "Type",
      type: "checkbox",
      options: contactType
        ? contactType
        : [
          { label: I18n.receivables || "Receivables", value: "Receivables" },
          { label: I18n.payables || "Payables", value: "Payables" },
          { label: I18n.settled || "Settled", value: "Settled" },
        ],
    },

    amount: {
      id: "amount",
      label: I18n.amount || "Amount",
      type: "amount",
      minPlaceholder: I18n.minimum_amount || "Minimum amount",
      maxPlaceholder: I18n.maximum_amount || "Maximum amount",
      applyLabel: I18n.apply || "APPLY",
    },

    keywords: {
      id: "keywords",
      label: I18n.search || "Search",
      type: "keywords",
      searchLabel: I18n.search || "SEARCH",
      placeholder: placeholder || I18n.search_by_contact || "Search by contact",
    },
  };

  return filters.map((f) => map[f.toLowerCase()]).filter(Boolean);
};

export const ValidateUploadedFile = ({
  file,
  allowedTypes = ["image/jpeg", "image/png", "image/jpg"],
  maxFileSize = 10,
  sizeType = "MB",
  I18n,
}) => {
  if (!file) {
    return {
      message: I18n?.file_not_selected || "No file selected",
      valid: false,
    };
  }

  const sizeLimit =
    sizeType.toUpperCase() === "MB"
      ? maxFileSize * 1024 * 1024
      : maxFileSize * 1024;

  if (!allowedTypes.includes(file.type)) {
    return {
      message: I18n.unsupported_file_format,
      valid: false,
    };
  }

  if (file.size > sizeLimit) {
    return {
      message: `${I18n?.file_size_validation || "File size must be within"
        } ${maxFileSize} ${sizeType}`,
      valid: false,
    };
  }

  return { message: I18n?.file_valid || "File is valid", valid: true };
};

export const formattedCount = (count) => {
  const n = Number(count) || 0;
  return n < 10 ? `0${n}` : n;
};

export const roundToTwoDecimalPlaces = (number) => {
  return parseFloat(number.toFixed(2));
};
export const formatPhoneForPKCode = (number) => {
  // Remove any non-numeric characters
  const cleanNumber = number.replace(/\D/g, "");

  // Check if the number starts with "03", then format it
  if (cleanNumber.startsWith("03") && cleanNumber.length === 11) {
    return `+92${cleanNumber.slice(1)}`;
  }

  return cleanNumber;
};

export const blurAllInputField = () => {
  const inputs = document.querySelectorAll("input, textarea, select");
  inputs.forEach((input) => input.blur()); // This blurs all the input fields
};

export const validString = (testString) => {
  const regex = /^[a-zA-Z0-9\s-_]+$/;
  return !testString || regex.test(testString);
};
export function removeAllSpaces(str) {
  // \s matches any whitespace, /g is global flag
  return str ? str.replace(/\s+/g, "") : "";
}

export const formatDate = (date) => {
  if (!date) return "";

  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export const sendToWhatsApp = ({
  businessName,
  phoneNumber,
  amount,
  bankName,
  accountTitle,
  accountNumber,
  iban,
}) => {
  const message = `Asalam o Alaikum!

You have an outstanding amount of Rs. ${amount} with ${businessName}.
Please pay from any of your bank account to the attached account. Thank you!

Bank: ${bankName}
Account title: ${accountTitle}
Account number: ${accountNumber}
IBAN: ${iban}`;

  const encodedMessage = encodeURIComponent(message);

  if (!phoneNumber) {
    window.open(`https://wa.me/?text=${encodedMessage}`, "_blank");
    return;
  }
  let cleaned = formatPhoneForPKCode(phoneNumber);
  window.open(`https://wa.me/${cleaned}?text=${encodedMessage}`, "_blank");
};


export const sendBanKDetailToWhatsApp = ({
  bankName,
  accountTitle,
  accountNumber,
  iban,
  qr_share_key
}) => {
  const message = `These are my bank account details:

  Account title: ${accountTitle}
  Account #: ${accountNumber}
  IBAN: ${iban}
  Bank: ${bankName || "RUPIN"}

  ${qr_share_key ? `QR code : ${process.env.REACT_APP_WEB_DOMAIN || "https://web.rupin.pk"}/qr/${qr_share_key}` : ""}

  Thanks
`;

  const encodedMessage = encodeURIComponent(message);
  window.open(`https://web.whatsapp.com/send?text=${encodedMessage}`, "_blank");
};


export const openFileInNewTab = (urlOrData) => {
  // Check if it’s a base64 string
  if (urlOrData.startsWith("data:")) {
    // Open base64 data in new tab
    const newWindow = window.open();
    if (newWindow) {
      newWindow.document.write(
        `<iframe src="${urlOrData}" frameborder="0" style="width:100%;height:100%;"></iframe>`,
      );
      newWindow.document.title = "Preview";
    } else {
      console.error("Popup blocked!");
    }
  } else {
    // HTTP/HTTPS URL
    window.open(urlOrData, "_blank");
  }
};

export const getAttachmentName = (attachment) => {
  if (!attachment) return "-";

  if (attachment?.name) return attachment.name;

  const file = attachment?.file || attachment; // in case attachment is just a URL string
  if (typeof file === "string" && file.startsWith("https")) {
    const parts = file.split(".");
    return `attachment.${parts[parts.length - 1]}`;
  }

  return "attachment.default";
};

export const getDueInfo = (date, dueText) => {
  if (!date) return { text: "-", isPast: false };

  const today = moment().startOf("day");
  const givenDate = moment(date).startOf("day");

  if (givenDate.isSame(today)) {
    return { text: "Due today", isPast: false };
  }

  if (givenDate.isSame(moment(today).add(1, "day"))) {
    return { text: "Due tomorrow", isPast: false };
  }

  if (givenDate.isBefore(today)) {
    return { text: dueText ? dueText : "Past due", isPast: true };
  }

  return {
    text: givenDate.format("DD-MM-YYYY"),
    isPast: false,
  };
};

export const handleShareDetails = async (
  businessName,
  accountInfo,
  selectedInvoice,
  navigate,
  isExpense = false,
  event,
  customer,
  amount,
  backPath,
) => {

  if (!accountInfo) {
    navigate("/UCash/VerifyPin");
    return;
  }

  if (isExpense) {
    const isVerifiedAccountForWallet =
      accountInfo?.emi_onboarding_status === EMIONBOARDINGSTATUS.VERIFIED &&
      accountInfo?.verified === VERIFIED.VERIFIED;

    triggerLogEvent(analytics, event || "record_payment_send_money");
    if (isVerifiedAccountForWallet && selectedInvoice) {
      const amountToSend = (amount && Number(amount) > 0)
        ? Number(amount)
        : selectedInvoice.amount - (selectedInvoice.advance_amount || 0);
      const invoiceUpdatePayload = {
        invoiceId: selectedInvoice.id,
        amountToSend,
        customerId: selectedInvoice._raw?.customer_id || null,
      };
      navigate("/UCash/SendMoney", { state: { invoiceUpdatePayload, backPath } });
    } else {
      navigate(isVerifiedAccountForWallet ? "/UCash/SendMoney" : "/WalletOnboarding");
    }
    return;
  }

  let customerDetail = customer
    ? customer
    : selectedInvoice
      ? await selectedInvoice.customer.fetch()
      : null;

  const raastDetail = accountInfo?.raast_info?.raast;
  const accountTitle = raastDetail?.raast_details?.name || "";
  const accountNumber = raastDetail?.raast_details?.account_number || "";
  const iban = raastDetail?.raast_details?.iban || "";
  const bank_name = raastDetail?.bank_name;

  sendToWhatsApp({
    businessName,
    phoneNumber: customerDetail?.phone,
    amount: formatNum(amount ? amount : selectedInvoice.amount),
    bankName: bank_name || "RUPIN",
    accountTitle: accountTitle,
    accountNumber: accountNumber,
    iban: iban,
  });
};

export const mapCategoryToList = (data = {}) => {
  return Object.entries(data).map(([key, value]) => ({
    label: key,
    value,
  }));
};

export const getReportFilterDisplayLabels = (I18n = {}) => ({
  "Today": I18n.today || "Today",
  "7 days": I18n.weekly || "Weekly",
  "30 days": I18n.monthly || "Monthly",
  "Half yearly": I18n.half_yearly || "6 Months",
  "Yearly": I18n.yearly || "Yearly",
});

export const formatRange = (filter, I18n = {}) => {
  if (!filter?.start_date) return I18n.select_date;

  const DURATION_DISPLAY_LABELS = {
    7: I18n.weekly || "Weekly",
    30: I18n.monthly || "Monthly",
    365: I18n.yearly || "Yearly",
    366: I18n.yearly || "Yearly",
  };

  const start = moment(filter.start_date).startOf("day");
  const end = moment(filter.end_date).startOf("day");
  const days = end.diff(start, "day") + 1;
  const isFullCalendarMonth =
    start.date() === 1 && end.isSame(start.clone().endOf("month").startOf("day"), "day");

  if (days === 1 && start.isSame(moment().startOf("day"), "day")) return I18n.today || "Today";
  if (DURATION_DISPLAY_LABELS[days]) return DURATION_DISPLAY_LABELS[days];
  if (isFullCalendarMonth) return I18n.monthly || "Monthly";

  const capFirst = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  return `${capFirst(moment(filter.start_date).format("MMM DD"))} - ${capFirst(moment(filter.end_date).format("MMM DD"))}`;
};

export const formatReportCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  const abs = `Rs. ${Math.abs(Number(value)).toLocaleString("en-US")}`;
  return value < 0 ? `-${abs}` : abs;
};

export const REPORT_DATE_FILTERS = ["Today", "7 days", "30 days", "Yearly"];

export const REPORT_PAGINATION_LIMIT = 20;

export const REPORT_SKELETON_ROWS = 10;

export const formatString = (input) => {
  let words = input.split('_');
  words[0] = words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return words.join(' ');
}

const CATEGORY_API_LANG = {
  en: "en",
  ur: "ur",
  romn: "roman",
};

const getCategoryApiLang = () => {
  const lang = localStorage.getItem("language") || "en";
  return CATEGORY_API_LANG[lang] || "en";
};

const getCachedCategoriesGroup = (groupKey) => {
  try {
    const raw = localStorage.getItem("categories");
    if (!raw) return {};
    const data = JSON.parse(raw);
    return data?.[groupKey] || {};
  } catch {
    return {};
  }
};

export const getCategoriesByType = (groupKey) => {
  const group = getCachedCategoriesGroup(groupKey);
  const apiLang = getCategoryApiLang();
  return Object.keys(group).map((id) => {
    const entry = group[id] || {};
    const name = entry.translations?.[apiLang] || entry.translations?.en || "";
    return {
      id,
      label: name,
      value: name,
      requires_fields: entry.requires_fields || [],
    };
  });
};

export const getCategoryNameById = (groupKey, categoryId) => {
  if (categoryId === undefined || categoryId === null || categoryId === "") return null;
  const group = getCachedCategoriesGroup(groupKey);
  const entry = group[String(categoryId)];
  if (!entry) return null;
  const apiLang = getCategoryApiLang();
  return entry.translations?.[apiLang] || entry.translations?.en || null;
};

// Resolves a stored category value for display: new records store the
// category id, so this looks up its translated name; legacy records stored
// a translated name string directly, so that name is matched back to its id
// to show it in the current language. Custom names with no match are shown as-is.
export const resolveCategoryDisplayName = (groupKey, categoryValue) => {
  if (!categoryValue) return categoryValue;
  const [legacyNameId] = findCategoryIdsByName(groupKey, categoryValue);
  return (
    getCategoryNameById(groupKey, categoryValue) ||
    getCategoryNameById(groupKey, legacyNameId) ||
    categoryValue
  );
};

// Every translated name (all languages) of the given category ids. Used so a
// query by name also matches legacy records that stored the name in another language.
export const getCategoryNamesByIds = (groupKey, ids = []) => {
  const group = getCachedCategoriesGroup(groupKey);
  return [
    ...new Set(
      ids.flatMap((id) =>
        Object.values(group[id]?.translations || {}).filter(Boolean).map(String),
      ),
    ),
  ];
};

// Given a display name (e.g. a P&L/CashFlow category bucket label), finds
// every category id in the group whose translation — in any language —
// matches it. Used so a query filtering/grouping by name can also match
// records that already store the newer id representation.
export const findCategoryIdsByName = (groupKey, name) => {
  if (!name) return [];
  const target = String(name).trim().toLowerCase();
  const group = getCachedCategoriesGroup(groupKey);
  return Object.keys(group).filter((id) => {
    const translations = group[id]?.translations || {};
    return Object.values(translations).some(
      (t) => String(t).trim().toLowerCase() === target,
    );
  });
};

export const getExtensionForImageUrl = (imageUrl = "") => {
  if (!imageUrl || typeof imageUrl !== "string") return "";

  // Base64 image: data:image/png;base64,...
  const base64Match = imageUrl.match(/^data:image\/([a-zA-Z0-9+.-]+);base64,/);

  if (base64Match?.[1]) {
    const ext = base64Match[1].toLowerCase();

    if (ext === "svg+xml") return "svg";
    if (ext === "jpeg") return "jpg";

    return ext;
  }

  // HTTP / HTTPS image URL
  try {
    const url = new URL(imageUrl);
    const pathname = url.pathname; // removes query params

    const match = pathname.match(/\.([a-zA-Z0-9]+)$/);

    return match?.[1]?.toLowerCase() || "";
  } catch {
    // Fallback for non-standard URLs
    const cleanUrl = imageUrl.split("?")[0].split("#")[0];
    const match = cleanUrl.match(/\.([a-zA-Z0-9]+)$/);

    return match?.[1]?.toLowerCase() || "";
  }
};

export const getStatementDateConfig = () => {
  const defaultStart = moment().startOf("day").toDate();
  const defaultEnd = moment().endOf("day").toDate();
  return {
    defaultStart,
    defaultEnd,
    minDate: dayjs().subtract(1, "year").startOf("day"),
    maxDate: dayjs().endOf("day"),
  };
};


// Urdu dates are shown right-to-left by the moment "ur" postformat in LanguageContext.
export const dateFormatInNumberWithSpace = (date) => {
  return date ? moment(date).format("DD MMM YYYY") : "";
};
export const formattedDateWithSpaceAndTime = (date) =>
  date ? moment(date).format("DD MMM YYYY h:mm A") : "";

// POS charge button: "CHARGE RS. 22 (SHIFT + ENTER)". The whole phrase comes from one key
// (charge_amount_shortcut) so each language sets its own word order. Urdu is wrapped in a
// right-to-left isolate so it reads from the right: "چارج روپے 22 (شفٹ + انٹر)".
export const getChargeButtonTitle = (I18n, amount) => {
  const title = (I18n.charge_amount_shortcut || "Charge RS. {amount} (Shift + Enter)")
    .replace("{amount}", formatNum(amount))
    .toUpperCase();
  return /[\u0600-\u06FF]/.test(title) ? `\u2067${title}\u2069` : title;
};

// "01 Oct 2025 at 7:10 PM". A literal English "at" breaks the Urdu right-to-left date,
// so Urdu uses its comma instead: "01 اکتوبر 2025، 7:10 دوپہر" (moment turns "," into "،").
export const formatDateAtTime = (date) =>
  date
    ? moment(date).format(
        moment.locale() === "ur" ? "DD MMM YYYY, h:mm A" : "DD MMM YYYY [at] h:mm A",
      )
    : "";

export const formatToISOString = (date) => {
  return moment(date).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
};

// Receipts are shared with customers, so capture them in light theme even when the app is in dark mode.
// html2canvas renders from a cloned document; flipping data-theme there leaves the live page untouched.
export const RECEIPT_CAPTURE_OPTIONS = {
  scale: 2,
  onclone: (clonedDoc) => {
    clonedDoc.documentElement.setAttribute("data-theme", "light");
    clonedDoc.documentElement.style.colorScheme = "light";
  },
};

export const downloadReceiptImage = (
  ref,
  { setIsDownloadLoading, analyticsEvent, fileName = "Rupin Transaction Receipt" },
) => {
  if (analyticsEvent) triggerLogEvent(analytics, analyticsEvent);
  setIsDownloadLoading(true);
  html2canvas(ref.current, RECEIPT_CAPTURE_OPTIONS).then((canvas) => {
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/jpeg", 1.0);
    link.download = `${fileName}.jpg`;
    link.click();
    setIsDownloadLoading(false);
  });
};

export const copyReceiptImage = (ref, { showSnackbar, I18n, analyticsEvent }) => {
  if (analyticsEvent) triggerLogEvent(analytics, analyticsEvent);
  html2canvas(ref.current, RECEIPT_CAPTURE_OPTIONS).then((canvas) => {
    canvas.toBlob((blob) => {
      navigator.clipboard
        .write([new ClipboardItem({ "image/png": blob })])
        .then(() => showSnackbar({ message: I18n.copied, severity: "success" }))
        .catch(() => showSnackbar({ message: I18n.failed_to_copy, severity: "error" }));
    }, "image/png");
  });
};

const POWERED_RUPIN_SVG = `<svg width="80" height="22" viewBox="0 0 80 22" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.67263 11.124C3.58189 11.124 3.51931 11.124 3.45638 11.124C2.4193 11.124 1.38221 11.1244 0.344777 11.1237C0.0131041 11.1233 -0.0849376 10.9606 0.0753362 10.671C1.08426 8.84756 2.09493 7.02478 3.10315 5.20095C3.17825 5.06501 3.27942 4.99965 3.43726 5C4.63671 5.00278 5.83615 5.0007 7.0356 5.00209C7.32173 5.00244 7.43055 5.17974 7.30122 5.43944C6.79015 6.46504 6.2756 7.48925 5.76245 8.51381C5.72907 8.58021 5.69778 8.64765 5.65398 8.73839C5.73255 8.74187 5.79235 8.74674 5.85215 8.74674C6.8489 8.74709 7.84531 8.74743 8.84207 8.74674C8.97696 8.74674 9.11603 8.73561 9.18834 8.88441C9.2617 9.03564 9.18278 9.14829 9.08961 9.25988C7.07349 11.6824 5.05912 14.1052 3.0444 16.5281C3.02215 16.5548 3.00059 16.582 2.97765 16.608C2.88274 16.7155 2.77392 16.7867 2.62755 16.7134C2.48257 16.6407 2.46727 16.5121 2.49891 16.3709C2.71968 15.3825 3.4981 11.9059 3.67263 11.124Z" fill="#F2BC1D"/>
<path d="M17.0939 15.5117C16.5899 15.5117 16.1326 15.409 15.7219 15.2037C15.3206 14.989 14.9986 14.695 14.7559 14.3217C14.5133 13.9484 14.3779 13.5237 14.3499 13.0477V11.0457C14.3779 10.5604 14.5133 10.1357 14.7559 9.7717C15.0079 9.39837 15.3346 9.10904 15.7359 8.9037C16.1466 8.68904 16.5993 8.5817 17.0939 8.5817C17.7006 8.5817 18.2466 8.7357 18.7319 9.0437C19.2266 9.3517 19.6139 9.76704 19.8939 10.2897C20.1739 10.8124 20.3139 11.4004 20.3139 12.0537C20.3139 12.707 20.1739 13.295 19.8939 13.8177C19.6139 14.3404 19.2266 14.7557 18.7319 15.0637C18.2466 15.3624 17.7006 15.5117 17.0939 15.5117ZM13.5939 15.3717V5.3757H14.8539V10.3457L14.6159 11.9557L14.8539 13.5797V15.3717H13.5939ZM16.8839 14.3217C17.3039 14.3217 17.6726 14.2237 17.9899 14.0277C18.3073 13.8317 18.5593 13.5657 18.7459 13.2297C18.9326 12.8844 19.0259 12.4877 19.0259 12.0397C19.0259 11.601 18.9326 11.209 18.7459 10.8637C18.5593 10.5184 18.3073 10.2524 17.9899 10.0657C17.6726 9.8697 17.3086 9.7717 16.8979 9.7717C16.4779 9.7717 16.1093 9.8697 15.7919 10.0657C15.4746 10.2524 15.2273 10.5184 15.0499 10.8637C14.8726 11.209 14.7839 11.6057 14.7839 12.0537C14.7839 12.4924 14.8679 12.8844 15.0359 13.2297C15.2133 13.5657 15.4606 13.8317 15.7779 14.0277C16.1046 14.2237 16.4733 14.3217 16.8839 14.3217ZM23.6803 15.4277L20.7963 8.7217H22.1963L24.3383 13.9717H23.8903L26.1443 8.7217H27.5443L24.4363 15.4277H23.6803ZM21.7763 18.1577L23.7363 14.1397L24.4363 15.4277L23.1763 18.1577H21.7763Z" fill="#334155"/>
<path d="M71.5133 6.7591L71.6365 6.77591V16.7871H69.7598V5H72.376L78.0735 15L77.9558 15.028V5H79.8214V16.7871H77.2051L71.5133 6.7591Z" fill="#334155"/>
<path d="M65.457 5H67.3338V16.7871H65.457V5Z" fill="#334155"/>
<path d="M55.4238 5H59.0485C59.788 5 60.4546 5.12325 61.0485 5.37535C61.6479 5.62745 62.1297 6.0084 62.4883 6.52941C62.8468 7.04482 63.0261 7.71149 63.0261 8.52941C63.0261 9.34734 62.8468 10.0252 62.4883 10.5742C62.1297 11.1176 61.6535 11.5266 61.0485 11.7955C60.4546 12.07 59.788 12.2045 59.0485 12.2045H57.3006V16.7871H55.4238V5ZM57.3006 10.6695H58.5331C58.9925 10.6695 59.407 10.6022 59.7824 10.4734C60.1633 10.3389 60.4658 10.1148 60.6843 9.80112C60.9084 9.493 61.0261 9.07843 61.0261 8.56863C61.0261 8.05322 60.914 7.64426 60.6843 7.35294C60.4658 7.05602 60.1633 6.84314 59.7824 6.71989C59.407 6.59664 58.9925 6.52941 58.5331 6.52941H57.3006V10.6695Z" fill="#334155"/>
<path d="M51.1208 5H52.9976V12.2941C52.9976 13.2633 52.8071 14.1036 52.4149 14.8151C52.034 15.5154 51.5018 16.0588 50.8183 16.4398C50.1348 16.8151 49.3337 17 48.4261 17C47.5186 17 46.723 16.8095 46.034 16.4398C45.3449 16.0588 44.8071 15.5154 44.4205 14.8151C44.034 14.1092 43.8379 13.2689 43.8379 12.2941V5H45.7146V12.1989C45.7146 12.8431 45.8323 13.3922 46.062 13.8515C46.2973 14.3109 46.6222 14.6583 47.0256 14.9048C47.4345 15.1513 47.9051 15.2689 48.4261 15.2689C48.9527 15.2689 49.4121 15.1457 49.8211 14.9048C50.2244 14.6583 50.5438 14.3109 50.7735 13.8515C51.0032 13.3922 51.1208 12.8431 51.1208 12.1989V5Z" fill="#334155"/>
<path d="M33.2168 5H37.5473C38.1972 5 38.7798 5.13445 39.3064 5.40896C39.8386 5.67787 40.2588 6.07003 40.5669 6.58544C40.8751 7.10084 41.0319 7.72829 41.0319 8.46779C41.0319 9.20168 40.8919 9.80112 40.6118 10.2829C40.3316 10.7591 39.9955 11.1176 39.6034 11.3529C39.2112 11.5882 38.8414 11.7227 38.5053 11.7563L41.4073 16.7815H39.2952L36.6174 11.8964H35.0992V16.7815H33.2224V5H33.2168ZM35.0935 10.5462H36.8807C37.5137 10.5462 38.0403 10.3894 38.4717 10.0756C38.8975 9.76191 39.116 9.2465 39.116 8.52941C39.116 7.80672 38.9031 7.29692 38.4829 6.9944C38.0627 6.69188 37.5305 6.54062 36.9031 6.54062H35.0992V10.5462H35.0935Z" fill="#334155"/>
</svg>`;

const buildWalletReceiptHtml = ({ headerText, amount, recipientName, customerName, bankInfo, rows = [] }) => `
<html>
  <head>
    <title>Rupin Receipt</title>
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Inconsolata:wght@200;300;400;500;600&display=swap" rel="stylesheet">
    <style>
      body { font-family: 'Inconsolata', monospace; color: #000; width: 100%; margin: 0; padding: 12px; box-sizing: border-box; }
      .card { text-align: center; }
      .headerText { margin: 0 0 20px; font-size: 13px; }
      .headerText strong { font-weight: 600; }
      .amountBlock { text-align: left; margin-bottom: 20px; }
      .amount { font-size: 22px; font-weight: 600; margin: 0 0 8px; }
      .recipient, .bankInfo { text-align: left; margin: 0; font-size: 13px; line-height: 18px; }
      .divider { border-top: 1px dashed #000; margin: 12px 0; }
      .detailsSection { text-align: left; }
      .row { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 10px; font-size: 13px; }
      .rowValue { text-align: right; }
      .rowLast .rowValue { font-weight: 500; }
      .poweredBy { display: flex; justify-content: center; margin-top: 16px; }
    </style>
  </head>
  <body>
    <div class="card">
      ${headerText ? `<p class="headerText" dir="auto">${headerText}</p>` : ""}
      <div class="amountBlock">
        <p class="amount">Rs. ${formatNum(amount)}</p>
        ${customerName ? `<p class="recipient">${customerName}</p>` : ""}
        ${recipientName ? `<p class="recipient">${recipientName}</p>` : ""}
        ${bankInfo ? `<p class="bankInfo">${bankInfo}</p>` : ""}
      </div>
      <div class="divider"></div>
      <div class="detailsSection">
        ${rows
          .map(
            (row, i) => `<div class="row${i === rows.length - 1 ? " rowLast" : ""}">
              <span>${row.label}</span>
              <span class="rowValue">${row.value}</span>
            </div>`,
          )
          .join("")}
      </div>
      <div class="poweredBy">${POWERED_RUPIN_SVG}</div>
    </div>
  </body>
</html>`;

/** Prints a wallet receipt via the same POS thermal-printer flow (checkConnection + qz-tray), matching the on-screen receipt card exactly. */
export const printWalletReceipt = (
  business,
  { headerText, amount, recipientName, customerName, bankInfo, rows },
  { showSnackbar, setIsPrintLoading, analyticsEvent } = {},
) => {
  setIsPrintLoading?.(true);
  checkConnection(business)
    .then((printers) => {
      if (printers[0] === "connected") {
        const config = qz.configs.create(business?.printer, {
          rasterize: false,
          scaleContent: false,
          density: "2",
        });
        const printFormat = [
          {
            type: "pixel",
            format: "html",
            flavor: "plain",
            data: buildWalletReceiptHtml({ headerText, amount, recipientName, customerName, bankInfo, rows }),
          },
        ];
        const doPrint = () =>
          qz
            .print(config, printFormat)
            .then(() => {
              if (analyticsEvent) triggerLogEvent(analytics, analyticsEvent);
            })
            .catch((err) => console.error("ERROR", err));

        if (!qz.websocket.isActive()) {
          qz.websocket
            .connect()
            .then(doPrint)
            .catch((err) => console.error("ERROR", err));
        } else {
          doPrint();
        }
      } else if (printers[0] === "off" || printers[0] === "not connected") {
        showSnackbar?.({ message: "Printer not connected", severity: "error", icon: "" });
      } else if (printers[0] === "not exist") {
        showSnackbar?.({ message: "Printer not exist", severity: "error", icon: "" });
      }
    })
    .catch((error) => {
      if (error === "not connected") {
        showSnackbar?.({ message: "Printer not connected", severity: "error", icon: "" });
      }
    })
    .finally(() => setIsPrintLoading?.(false));
};

/** Opens a print-view window for a wallet transaction receipt — same pattern as
 * ShowQR's handlePrintQrCode (a real browser print dialog the user can preview
 * and confirm), not a direct silent print — matching the on-screen receipt card. */
export const printWalletReceiptPreview = (
  business,
  { headerText, amount, recipientName, customerName, bankInfo, rows },
  { showSnackbar, setIsPrintLoading, analyticsEvent } = {},
) => {
  setIsPrintLoading?.(true);
  const printWindow = window.open("", "", "width=380,height=600");
  if (!printWindow) {
    showSnackbar?.({ message: "Unable to open print preview", severity: "error", icon: "" });
    setIsPrintLoading?.(false);
    return;
  }
  printWindow.document.write(
    buildWalletReceiptHtml({ headerText, amount, recipientName, customerName, bankInfo, rows }),
  );
  printWindow.document.close();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
    if (analyticsEvent) triggerLogEvent(analytics, analyticsEvent);
    setIsPrintLoading?.(false);
  }, 500);
};

export const clearLocalStorageExcludeSomeFields = () => {
  const rupinMigrationBannerLoginCount = localStorage.getItem("rupinMigrationBannerLoginCount");
  const rupinMigrationBannerDismissed = localStorage.getItem("rupinMigrationBannerDismissed");
  const rupinOnboardingAnimationSeen = localStorage.getItem("rupinOnboardingAnimationSeen")
  const rupinMigrationModalSeen = localStorage.getItem("rupinMigrationModalSeen")
  const theme = localStorage.getItem("theme")
  // Only a valid code is kept, so an old "null"/"[object Promise]" value is dropped here
  const language = getValidStoredLanguage()

  localStorage.clear();
  localStorage.setItem("rupinMigrationBannerLoginCount", rupinMigrationBannerLoginCount || 0)
  localStorage.setItem("rupinMigrationBannerDismissed", rupinMigrationBannerDismissed || false)
  localStorage.setItem("rupinOnboardingAnimationSeen", rupinOnboardingAnimationSeen || false)
  localStorage.setItem("rupinMigrationModalSeen", rupinMigrationModalSeen || false)
  if (theme) localStorage.setItem("theme", theme)
  if (language) localStorage.setItem("language", language)
}